import { format, parseISO, subDays } from 'date-fns';
import {
  HttpError,
  type HttpClientResponse,
  type HttpRequestConfig,
} from '../HttpClient';
import HttpClient from '../HttpClient';
import HTTPResponse from '../HttpResponse';
import { ERRORS, type ErrorResponse } from '../errors';
import {
  type ServicesResponse,
  type AvailableStore,
  type StoresResponse,
  type AppointmentResponse,
  type PayOrderLinkResponse,
  type Payload,
  type AvailableServicesInZipResponse,
  type AvailableTimeslotsResponse,
  type TimeSlotsCapacity,
  type ConfirmAppointmentParams,
} from './model';
import { toAvailableService, toAvailableStore } from './utils';
import { isStoreService, type ServiceLocation } from '../../helpers/appHelper';
import { type CustomerType } from '../../reducers/user/userTypes';
import { isBusinessOperation } from '../../helpers/operationsHelper';
import { getQueryParamsFromURL } from '../../helpers/urlHelper';

/* globals CSS_CONFIG */

const MAX_REGISTRATION_CLOSING_DAYS_PLUS_ONE = 6;
const MILLISECONDS_REGEX = /\.000/;

const isEmptyResponse = (
  response: HttpClientResponse<AvailableServicesInZipResponse[]> | null,
): boolean => {
  return (
    !response || !Array.isArray(response.data) || response.data.length === 0
  );
};

class Appointment {
  private httpBokaCustomer: HttpClient;

  private httpBokaCoworker: HttpClient;

  public constructor(
    httpBokaCustomer: HttpClient,
    httpBokaCoworker: HttpClient,
  ) {
    this.httpBokaCustomer = httpBokaCustomer;
    this.httpBokaCoworker = httpBokaCoworker;
  }

  private getServices = async (
    excludeIbnPrices: boolean,
    zip: string,
    operation: string,
    stateCode?: string,
  ): Promise<HttpClientResponse<AvailableServicesInZipResponse[]>> => {
    const priceTypeURLParam = excludeIbnPrices
      ? '?priceType=Individual'
      : '?priceType=null';
    const stateCodeURLParam =
      CSS_CONFIG.services.getServicesUsingStateCode && stateCode
        ? `&stateCode=${stateCode}`
        : '';
    return this.httpBokaCustomer.get(
      `/servicesbygroup/${zip}/${operation.toUpperCase()}${priceTypeURLParam}${stateCodeURLParam}`,
    );
  };

  public getAvailableServices = async (
    customerType: CustomerType,
    allowedMemberDiscounts: boolean,
    zip: string,
    operation: string,
    stateCode?: string,
  ): Promise<HTTPResponse<ServicesResponse>> => {
    const isBusiness = isBusinessOperation(operation);
    const excludeIbnPrices = !isBusiness && customerType === 'Individual';
    try {
      const response = await this.getServices(
        excludeIbnPrices,
        zip,
        operation,
        stateCode,
      );

      if (isEmptyResponse(response)) {
        return new HTTPResponse(
          { services: [] },
          ERRORS.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      const servicesResponse = {
        services: response.data.map(
          toAvailableService(
            operation,
            customerType,
            allowedMemberDiscounts,
            CSS_CONFIG,
          ),
        ),
      };

      return new HTTPResponse(servicesResponse);
    } catch (error) {
      return new HTTPResponse({ services: [] }, ERRORS.SERVICE_NOT_AVAILABLE);
    }
  };

  public getAvailableTimeslots = async (
    zip: string,
    type: string,
    location: ServiceLocation,
    storeId: string,
    from: string,
    serviceCode: string,
    nbrOfDays?: number,
    language?: string,
  ): Promise<HTTPResponse<AvailableTimeslotsResponse[]>> => {
    const { walkin } = getQueryParamsFromURL();
    const urlParts =
      isStoreService(location) && storeId
        ? [`/services/${zip}/${type}/${storeId}?serviceCode=${serviceCode}`]
        : [`/services/${zip}/${type}?serviceCode=${serviceCode}`];

    const formattedFrom = format(parseISO(from), 'yyyy-MM-dd');
    urlParts.push(`&from=${formattedFrom}`);
    const nbrOfDaysParam = nbrOfDays ? `&noOfDays=${nbrOfDays}` : '';
    urlParts.push(nbrOfDaysParam);
    const walkinParam = walkin ? `&includeWalkin=true` : '';
    urlParts.push(walkinParam);

    // Use language selected in language picker if available
    const config: HttpRequestConfig = language
      ? {
          headers: {
            'country-Language': `${CSS_CONFIG.country}-${language}`,
          },
        }
      : {};

    try {
      const result = await this.httpBokaCustomer.get<
        AvailableTimeslotsResponse[]
      >(urlParts.join(''), config);
      return new HTTPResponse(result.data);
    } catch (err: unknown) {
      if (err instanceof HttpError) {
        const error =
          Object.values(ERRORS).indexOf(err.response.status) > -1
            ? err.response.status
            : ERRORS.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse([], error);
      }
      return new HTTPResponse([], ERRORS.SERVICE_NOT_AVAILABLE);
    }
  };

  public getAvailableStores = async (
    zip: string,
    serviceProductId: string,
  ): Promise<HTTPResponse<StoresResponse>> => {
    try {
      const url = `/locations/${zip}/${serviceProductId}`;
      const result = await this.httpBokaCustomer.get(url);

      if (result.status < 200 || result.status >= 300) {
        const error =
          Object.values(ERRORS).indexOf(result.status) > -1
            ? result.status
            : ERRORS.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse({ stores: [] }, error);
      }
      if (!Array.isArray(result.data) || result.data.length === 0) {
        return new HTTPResponse(
          { stores: [] },
          ERRORS.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      const stores: AvailableStore[] = result.data.map(toAvailableStore);
      return new HTTPResponse({ stores });
    } catch (err: unknown) {
      if (err instanceof HttpError) {
        const error =
          Object.values(ERRORS).indexOf(err.response.status) > -1
            ? err.response.status
            : ERRORS.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse({ stores: [] }, error);
      }
      return new HTTPResponse({ stores: [] }, ERRORS.SERVICE_NOT_AVAILABLE);
    }
  };

  public confirmAppointment = async (
    {
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
      selectedLanguage,
      source,
    }: ConfirmAppointmentParams,
    config: HttpRequestConfig,
  ): Promise<HTTPResponse<AppointmentResponse>> => {
    try {
      const mergedConfig: HttpRequestConfig = {
        ...config,
        headers: {
          ...config.headers,
          ...(selectedLanguage && {
            'country-Language': `${CSS_CONFIG.country}-${selectedLanguage}`,
          }),
        },
      };

      let url = `/services/${zipCode}/${serviceId}/${storeId}/${timeSlotId}?serviceCode=${serviceCode}`;

      const formattedFrom = format(
        subDays(parseISO(from), MAX_REGISTRATION_CLOSING_DAYS_PLUS_ONE),
        'yyyy-MM-dd',
      );
      url = `${url}&from=${formattedFrom}&source=${source}`;

      const { data, status } = await this.httpBokaCustomer.post<
        AppointmentResponse,
        Payload
      >(url, payload, mergedConfig);

      // 208 is a successful request; but we want to show the user an error.
      // So we return early with an "error object" so that we can derive the error status from the returned object.
      if (status === ERRORS.ALREADY_REPORTED) {
        return new HTTPResponse(data, {
          status: ERRORS.ALREADY_REPORTED,
          data: { ...data },
        });
      }

      if (status === ERRORS.FORBIDDEN) {
        return new HTTPResponse(data, {
          status: ERRORS.FORBIDDEN,
          data: { ...data },
        });
      }

      return new HTTPResponse(data);
    } catch (err: unknown) {
      const appointmentResponse: AppointmentResponse = {
        orderNo: '',
        payOrderLink: '',
        questionsToken: '',
        payOrderLinkEnabled: true,
        additionalInfoLinkEnabled: false,
      };
      if (err instanceof HttpError) {
        const error: ErrorResponse = {
          status:
            Object.values(ERRORS).indexOf(err.response.status) > -1
              ? err.response.status
              : ERRORS.SERVICE_NOT_AVAILABLE,
          data: err.response.data || {},
        };
        return new HTTPResponse(appointmentResponse, error);
      }
      return new HTTPResponse(
        appointmentResponse,
        ERRORS.SERVICE_NOT_AVAILABLE,
      );
    }
  };

  public async getPayOrderLink(
    orderNumber: string,
    email: string,
  ): Promise<string> {
    const encodedEmail = encodeURIComponent(email);
    try {
      const response: HttpClientResponse<PayOrderLinkResponse> =
        await this.httpBokaCustomer.get(
          `/payorderlink?orderNumber=${orderNumber}&email=${encodedEmail}`,
        );
      const link = response?.data?.link ?? '';
      return link;
    } catch (error) {
      return '';
    }
  }

  public getIANATimeZone = async (
    postalCode: string,
    serviceProductId: string,
  ): Promise<string> => {
    try {
      const response: HttpClientResponse<string> =
        await this.httpBokaCoworker.get(
          `/services/timezone/${postalCode}/${serviceProductId}`,
        );

      const timeZone = response?.data;
      return timeZone;
    } catch (error) {
      return '';
    }
  };

  public getTimeslotsToday = async (
    zip: string,
    storeId: string,
    serviceCode: string,
    serviceId: string,
  ): Promise<AvailableTimeslotsResponse[]> => {
    try {
      // Temporary: force walk-in slots until stores refresh to URLs with walkin=true.
      // const { walkin } = getQueryParamsFromURL();
      // const walkinParam = walkin ? '&includeWalkin=true' : '';
      const walkinParam = '&includeWalkin=true';
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await this.httpBokaCustomer.get(
        `services/${zip}/${serviceId}/${storeId}?serviceCode=${serviceCode}&from=${today}${walkinParam}`,
      );
      if (Array.isArray(response.data)) {
        return response.data
          .filter((item) => item.from.startsWith(today))
          .slice(0, 9);
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  public getTimeSlotCapacity = async (
    postalCodeForLocation: string,
    serviceProductId: string,
    from: string,
    isTimeInBrowserSettings: boolean,
    language?: string,
  ): Promise<TimeSlotsCapacity[]> => {
    const ianaTimeZone = isTimeInBrowserSettings
      ? `&ianaTimeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
      : '';

    const { walkin } = getQueryParamsFromURL();
    const walkinParam = walkin ? `&includeWalkin=true` : '';

    // Use language selected in language picker if available
    const config: HttpRequestConfig = language
      ? {
          headers: {
            'country-Language': `${CSS_CONFIG.country}-${language}`,
          },
        }
      : {};

    try {
      const urlParts = [
        '/timewindow/capacity',
        `?postalCode=${postalCodeForLocation}`,
        `&serviceProductId=${serviceProductId}`,
        `&countryCode=${CSS_CONFIG.country}`,
        `&language=${language || CSS_CONFIG.language}`,
        `&startDateTimeUTC=${from.replace(MILLISECONDS_REGEX, '')}`,
        `${ianaTimeZone}`,
        `${walkinParam}`,
      ];
      const response: HttpClientResponse<TimeSlotsCapacity[]> =
        await this.httpBokaCoworker.get(urlParts.join(''), config);
      return response.data;
    } catch (error) {
      return [];
    }
  };
}

export default Appointment;
